import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { EAST4 } from "./utility/enums";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};


firebase.initializeApp(firebaseConfig);

const firestore = firebase.firestore();

// Auth is set up here, before firebase.auth(), with the same storage the
// compat SDK uses (IndexedDB, then local/session storage, then memory) but
// without the popup/redirect helper. On phones and in Safari that helper
// loads Google's API script and an iframe from firebaseapp.com and waits for
// them before saying whether anyone is signed in; the whole app sat on its
// loader for that time (over a second on a mid-range phone), and the home page
// was drawn twice. The site only signs in with email and password, so nothing
// needs the helper. firebase.auth() picks up this instance.
initializeAuth(firebase.app(), {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence, inMemoryPersistence],
});

const auth = firebase.auth();

// Settles the first time Auth says who is signed in (also when no one is).
// index.js waits for it before drawing over a prerendered page, so the app's
// first render can be the page itself rather than the "who is this?" loader.
let authKnown = false;
const authReady = new Promise((resolve) => {
  const stop = auth.onAuthStateChanged(() => {
    authKnown = true;
    resolve();
    if (stop) stop();
  });
});
// True once Auth has answered and the answer is "no one".
const signedOutKnown = () => authKnown && !auth.currentUser;

const storage = firebase.storage();

// Firebase Analytics (GA4) starts once the page has loaded and the browser is
// idle, together with the Google tag in public/index.html, whose gtag.js it
// reuses. Its library is no longer part of the code every page waits for.
const startAnalytics = () => {
  try {
    if (window.__startThirdParty) {
      window.__startThirdParty();
    }
  } catch (e) {}
  import("firebase/compat/analytics")
    .then(() => {
      const analytics = firebase.analytics();
      analytics.logEvent('notification_received');
    })
    .catch(() => {});
};
if (typeof window !== "undefined") {
  const whenIdle = () => (window.requestIdleCallback || ((f) => setTimeout(f, 1)))(startAnalytics, { timeout: 3000 });
  if (document.readyState === "complete") {
    whenIdle();
  } else {
    window.addEventListener("load", whenIdle);
  }
}

const fieldValue = firebase.firestore.FieldValue;

const emailAuthProvider = firebase.auth.EmailAuthProvider;

let httpUrl = process.env.REACT_APP_HTTP_URL;
let httpUrl_EAST4= process.env.REACT_APP_HTTP_URL_EAST4;
// let httpUrl_West3= "https://us-west3-project-everbary.cloudfunctions.net/";


let STRIPE_ID = process.env.REACT_APP_STRIPE_ID;
// The payment method configuration the server creates PaymentIntents with
// (functions STRIPE_PAYMENT_CONFIG). The checkout's card form is shown before
// the PaymentIntent exists, so it needs the same one to offer the same methods.
let STRIPE_PAYMENT_CONFIG = process.env.REACT_APP_STRIPE_PAYMENT_CONFIG || "";

let PAYPAL_OPTIONS = {
                "client-id": 
                process.env.REACT_APP_PAYPAL_CLIENT_ID,
              }

// Algolia search clients are made per person in src/utility/searchClient.js.


// // eslint-disable-next-line no-restricted-globals
if (process.env.REACT_APP_ENV === "development") {
  console.log("localhost detected!");
  auth.useEmulator("http://127.0.0.1:9099");
  firestore.useEmulator("127.0.0.1", 8080);
  storage.useEmulator("127.0.0.1", 9199);
}

// Development and staging have no search index.
const hasSearchIndex = () => {
  if(process.env.REACT_APP_ENV === "development" || process.env.REACT_APP_ENV === "staging"){
    return false;
  }
  return true;
}

// Whether the lists find their rows through search. Without an index they
// read Firestore directly. REACT_APP_LISTS_FROM_SEARCH=true (development
// only) makes them take the search path anyway, with the client that finds
// nothing: what production shows while the index is behind, to test it.
const checkFetchAlgoliaData = () => {
  if(process.env.REACT_APP_ENV === "development" && process.env.REACT_APP_LISTS_FROM_SEARCH === "true"){
    return true;
  }
  return hasSearchIndex();
}

const getFullHttpUrl = (path, region) => {
  const fullUrl = region === EAST4 ? `${httpUrl_EAST4}${path}` : `${httpUrl}${path}`;
  return fullUrl;
}

const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "https://www.everbary.com";


export { authReady, signedOutKnown };
export { firestore, auth, storage, fieldValue, emailAuthProvider, getFullHttpUrl, STRIPE_ID, STRIPE_PAYMENT_CONFIG, PAYPAL_OPTIONS, checkFetchAlgoliaData, hasSearchIndex, websiteUrl };